import type { FC } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiCorePackageV3 } from '@wix/da-papi-types';
import { formatDollars } from '@wix/da-shared-react/pkg/utils/formatting';

import s from './CoreRecurringPriceAndCycle.scss';

interface Props {
  className?: string;
  corePackage: PapiCorePackageV3;
  priceStyle?: 'regular' | 'bold' | 'extra-bold';
  priceDisplayMode?: 'original' | 'discounted' | 'both'; // 'original': show original price, 'discounted': show only discounted price, 'both': show original (strikethrough) + discounted
  reverseOrder?: boolean; // true: [Discounted price] - [~~Original price~~], false: [~~Original price~~] - [Discounted price]
}

const CoreRecurringPriceAndCycle: FC<Props> = ({
  className,
  corePackage,
  priceStyle = 'regular',
  priceDisplayMode = 'original',
  reverseOrder,
}) => {
  const { t } = useTranslation();
  const { interval, monthlyPrice, monthlyPriceBeforeDiscount } = corePackage;

  const isDisabled = corePackage.status === 'disabled';

  const showOriginalPrice =
    priceDisplayMode === 'both' && monthlyPriceBeforeDiscount;
  const showDiscountedPrice =
    priceDisplayMode === 'discounted' || priceDisplayMode === 'both';
  const displayPrice = showDiscountedPrice
    ? monthlyPrice
    : (monthlyPriceBeforeDiscount ?? monthlyPrice);

  return (
    <div
      className={cn(
        s['root'],
        isDisabled && s['disabled'],
        reverseOrder && s['reverse-order'],
        className
      )}
    >
      {showOriginalPrice && (
        <div className={s['price-before-discount']}>
          {formatDollars(monthlyPriceBeforeDiscount)}
        </div>
      )}
      <div className={cn(s['price'], s[priceStyle])}>
        {formatDollars(displayPrice)}
      </div>
      {interval === 'year' ? (
        <>
          <div className={s['times']}>&times;</div>
          <div>{t('packagePicker.billingCycle.new.12months')}</div>
        </>
      ) : (
        priceDisplayMode ===
          'original' /* monthly discount applies for only one month */ && (
          <>
            <div className={s['slash']}>/</div>
            <div>{t('packagePicker.billingCycle.new.month')}</div>
          </>
        )
      )}
    </div>
  );
};
CoreRecurringPriceAndCycle.displayName = 'CoreRecurringPriceAndCycle';

export default CoreRecurringPriceAndCycle;
